/**
 * Vocea cu care vorbește agentul. Două surse, în ordinea priorității:
 *
 * 1. `?voice=1|2|3` în URL — override de test, lipicios pe durata TABULUI
 *    (`stickyParam.ts`). Se dictează la telefon („pune voice egal doi"), de-aia
 *    cifre, nu nume. Era `?aig_voice=` până pe 19 aug 2026; s-a scurtat la
 *    cererea lui Radu — mai simplu de reținut, iar validarea strictă (doar
 *    1|2|3) face coliziunea cu un `?voice=` al sitului client inofensivă.
 * 2. Setarea per-app din dashboard (`branding.voice`, valori semantice
 *    `standard|serious|storyteller` — vezi `WIDGET_VOICES` în shared/config.ts).
 *    Alegerea clientului, servită prin `/config`.
 *
 * Fără niciuna: vocea agentului („standard"), exact ce aude un vizitator real.
 *
 * Istoric: până pe 10 aug 2026 alegerea se făcea din chips-uri desenate peste
 * pagină (`VoicePicker`, dispărut) și nu era lipicioasă. Motivul lipiciului e
 * concret: o conversație VOCALĂ navighează prin magazin (`open_search`,
 * `navigate`), fiecare navigare descarcă documentul și pierde query-ul — deci
 * vocea s-ar schimba în mijlocul propoziției.
 */

import type { WidgetVoice } from "@ai-guidance/shared";

/**
 * Cheile din `ELEVENLABS_VOICES` (backend, `services/elevenlabs.ts`), plus
 * "agent" - care NU e o voce, ci absența override-ului: vocea configurată pe
 * agentul de la ElevenLabs, adică exact ce aude un vizitator real. Cheia asta
 * nu pleacă niciodată în cerere; controlerul o traduce în „câmp lipsă".
 */
export type VoiceName = "agent" | "deep" | "gravelly";

/**
 * Numele parametrului, într-un singur loc: îl citește `sdk.ts` (din URL, prin
 * `stickyParam.ts`) și îl SCRIE comanda din card (`/voice-2`, vezi
 * `orbchat/index.ts`). Două porți spre aceeași alegere, deci o singură cheie.
 */
export const VOICE_PARAM = "voice";

/** Ce alege o comandă `/voice` fără cifră - sau cu una care nu există în set. */
export const VOICE_FALLBACK_PARAM = "2";

/**
 * Valorile acceptate în URL. 1 e vocea agentului: demonstrația începe cu exact
 * ce aude un vizitator real, iar comutarea e ceva ce ARĂȚI.
 */
const VOICE_BY_PARAM: Record<string, VoiceName> = {
  "1": "agent",
  "2": "deep",
  "3": "gravelly",
};

/**
 * Traducerea setării din dashboard (`branding.voice`) în cheia de provider.
 * Aceeași ordine ca `VOICE_BY_PARAM` (standard=1, serious=2, storyteller=3),
 * ca linkul de test și selectul din dashboard să vorbească despre aceleași
 * voci cu aceleași numere.
 */
const VOICE_BY_SETTING: Record<WidgetVoice, VoiceName> = {
  standard: "agent",
  serious: "deep",
  storyteller: "gravelly",
};

/** Vocea de pe care pleacă o pagină fără `?voice=` și fără setare în app. */
export const DEFAULT_VOICE_NAME: VoiceName = "agent";

/** `?voice=` e o valoare din setul de mai sus sau nimic - niciun string arbitrar. */
export function isVoiceParam(value: unknown): value is string {
  return typeof value === "string" && value in VOICE_BY_PARAM;
}

/**
 * Traduce valoarea din URL/storage în vocea cerută. Orice altceva (lipsă,
 * „deep", „4", gunoi) cade pe implicit - nimic nevalidat nu ajunge la backend,
 * care oricum revalidează cheia contra allow-list-ului providerului.
 */
export function voiceNameFromParam(value: unknown): VoiceName {
  return isVoiceParam(value) ? VOICE_BY_PARAM[value]! : DEFAULT_VOICE_NAME;
}

/**
 * Traduce setarea per-app în cheia de provider. `null` (nu default) când
 * lipsește sau nu e din set: apelantul (`sdk.ts`) decide căderea mai departe,
 * fiindcă între setare și implicit mai stă override-ul din URL.
 */
export function voiceNameFromSetting(value: unknown): VoiceName | null {
  return typeof value === "string" && value in VOICE_BY_SETTING ? VOICE_BY_SETTING[value as WidgetVoice] : null;
}
